
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAllOrders } from '../context/AllOrdersContext';
import { AppContext } from '../context/AppContext';
import { DeliveryIcon, OrderIcon, UserIcon, RestaurantIcon } from '../components/icons';
import { formatDateReadable } from '../utils/dateUtils';


const LivreurOrdersScreen: React.FC = () => {
    const { currentUser } = useAuth();
    const { placedOrders, loading: ordersLoading } = useAllOrders();
    const { setView } = React.useContext(AppContext);
    const [activeTab, setActiveTab] = useState<'active' | 'history'>('active');

    const driverOrders = placedOrders.filter(o => o.assignedDriver?.id === currentUser?.uid);

    const activeMissions = driverOrders.filter(o =>
        o.livreurStatus === 'accepted' &&
        ['En attente', 'En préparation', 'Prête', 'Envoyée'].includes(o.status)
    ).sort((a, b) => {
        const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return dateB - dateA;
    });

    const historyMissions = driverOrders.filter(o =>
        o.status === 'Livrée' || o.status === 'Terminée' || o.status === 'Annulée' || o.livreurStatus === 'rejected'
    ).sort((a, b) => {
        const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return dateB - dateA;
    });



    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Header Area */}
            <div className="bg-white px-6 pt-8 pb-6 sticky top-0 z-20">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">
                        Mes <span className="text-red-600">Missions</span>
                    </h1>
                </div>

                {/* Premium Tab Bar */}
                <div className="flex bg-gray-100/80 p-1.5 rounded-[1.5rem] backdrop-blur-md border border-gray-200/50">
                    <button
                        onClick={() => setActiveTab('active')}
                        className={`flex-1 py-3 text-[10px] font-black uppercase italic tracking-[0.2em] rounded-2xl transition-all ${activeTab === 'active' ? 'bg-black text-white shadow-xl shadow-gray-400/20' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        Actives ({activeMissions.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`flex-1 py-3 text-[10px] font-black uppercase italic tracking-[0.2em] rounded-2xl transition-all ${activeTab === 'history' ? 'bg-black text-white shadow-xl shadow-gray-400/20' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                        Historique
                    </button>
                </div>
            </div>

            <div className="px-6">
                {ordersLoading ? (
                    <div className="flex flex-col items-center justify-center py-24 gap-4">
                        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin shadow-xl"></div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest italic animate-pulse">Syncing Mission Hub...</p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {activeTab === 'active' ? (
                            activeMissions.length > 0 ? (
                                activeMissions.map(order => (
                                    <div
                                        key={order.id}
                                        onClick={() => setView({ screen: 'livreurActiveOrder', params: { orderId: order.id } })}
                                        className="group bg-white p-6 rounded-[2.5rem] shadow-[0_15px_35px_-10px_rgba(0,0,0,0.1)] border border-gray-100 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)] transition-all active:scale-[0.98] relative overflow-hidden"
                                    >
                                        {/* Status Badge Background */}
                                        <div className="absolute top-0 right-0 py-2 px-6 bg-red-600 text-white transform rotate-1 rounded-bl-[2rem] shadow-lg">
                                            <span className="text-[9px] font-black uppercase italic tracking-widest">{order.status}</span>
                                        </div>

                                        <div className="flex items-center gap-5">
                                            <div className="w-16 h-16 bg-black text-white rounded-[1.5rem] flex items-center justify-center shadow-2xl relative overflow-hidden">
                                                <div className="absolute top-0 right-0 w-8 h-8 bg-white/10 rounded-full -mr-4 -mt-4" />
                                                <DeliveryIcon className="w-8 h-8" />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-1 italic">Restaurant</p>
                                                <p className="text-xl font-black text-gray-900 uppercase tracking-tighter italic leading-none">{order.items[0]?.restaurantName}</p>
                                            </div>
                                        </div>

                                        <div className="mt-8 pt-6 border-t border-dashed border-gray-100 flex justify-between items-end">
                                            <div>
                                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest italic mb-1">Paiement</p>
                                                <p className="text-2xl font-black text-gray-900 italic tracking-tighter leading-none">{order.deliveryFee} <span className="text-[10px]">DA</span></p>
                                            </div>
                                            <div className="flex items-center gap-2 bg-gray-50 px-5 py-3 rounded-2xl border border-gray-100 group-hover:bg-red-600 group-hover:border-red-600 transition-all">
                                                <span className="text-[10px] font-black uppercase italic tracking-widest text-gray-400 group-hover:text-white transition-colors">Mission Hub</span>
                                                <span className="text-gray-300 group-hover:text-white transition-colors">→</span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-24 flex flex-col items-center">
                                    <div className="w-24 h-24 bg-gray-50 rounded-[2rem] flex items-center justify-center mb-8 shadow-inner relative overflow-hidden border border-gray-100">
                                        <div className="absolute top-0 right-0 w-12 h-12 bg-white rounded-full -mr-6 -mt-6" />
                                        <span className="text-5xl grayscale opacity-20">🛵</span>
                                    </div>
                                    <h3 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic mb-3">En attente de mission</h3>
                                    <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest italic mb-8 max-w-[200px] leading-loose">Restez à l'écoute, les nouvelles commandes arrivent vite.</p>
                                    <button
                                        onClick={() => setView({ screen: 'livreurDashboard' })}
                                        className="px-10 py-5 bg-black text-white font-black rounded-2xl text-[10px] uppercase italic tracking-[0.2em] active:scale-95 transition-all shadow-2xl shadow-gray-200"
                                    >
                                        Dashboard Livreur
                                    </button>
                                </div>
                            )
                        ) : (
                            historyMissions.length > 0 ? (
                                historyMissions.map(order => (
                                    <div key={order.id} className="bg-white p-5 rounded-[2rem] shadow-sm border border-gray-100 flex items-center justify-between opacity-70 grayscale hover:grayscale-0 hover:opacity-100 transition-all">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center">
                                                <OrderIcon className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <p className="text-base font-black text-gray-900 uppercase tracking-tighter italic">{order.items[0]?.restaurantName}</p>
                                                <p className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">
                                                    {formatDateReadable(order.timestamp)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-black text-gray-900 italic tracking-tighter">{order.deliveryFee} DA</p>
                                            <p className={`text-[9px] font-black uppercase italic tracking-widest px-2 py-0.5 rounded-full border ${order.status === 'Annulée' ? 'text-red-400 border-red-100' : 'text-green-500 border-green-100'}`}>
                                                {order.status === 'Annulée' ? 'ANNULÉ' : 'TERMINÉ'}
                                            </p>
                                            <div className="flex flex-col gap-1 items-end mt-2">
                                                {order.driverRating && (
                                                    <div className="flex items-center gap-1.5 bg-blue-50/50 px-2 py-1 rounded-xl border border-blue-100">
                                                        <span className="text-[10px] font-black text-blue-600 italic">{order.driverRating}</span>
                                                        <UserIcon className="w-3 h-3 text-blue-400" />
                                                    </div>
                                                )}
                                                {order.restaurantDriverRating && (
                                                    <div className="flex items-center gap-1.5 bg-green-50/50 px-2 py-1 rounded-xl border border-green-100">
                                                        <span className="text-[10px] font-black text-green-600 italic">{order.restaurantDriverRating}</span>
                                                        <RestaurantIcon className="w-3 h-3 text-green-400" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-20 flex flex-col items-center">
                                    <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4 border border-dashed border-gray-200">
                                        <OrderIcon className="w-6 h-6 text-gray-200" />
                                    </div>
                                    <p className="text-gray-400 font-bold text-[10px] uppercase tracking-[0.2em] italic">Historique vide</p>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LivreurOrdersScreen;
